import React, { Component } from 'react';
import {
  graphql,
  createPaginationContainer,
  type RelayPaginationProp,
} from 'react-relay';
import { bindActionCreators } from 'redux';
import { formValueSelector, initialize, reset } from 'redux-form';
import { TableRow, TableRowColumn } from 'material-ui/Table';
import RaisedButton from 'material-ui/RaisedButton';
import CreateIcon from 'material-ui/svg-icons/content/create';
import IconButton from 'material-ui/IconButton';
import PhoneIcon from 'material-ui/svg-icons/communication/phone';

import MailTo from '../../components/MailTo';
import Grid from '../../components/Grid';
import enhancer from '../../enhancer';
import StudentsFilter from './components/StudentsFilter';

import type { Students_admin } from './__generated__/Students_admin.graphql';

type Props = {
  admin: Students_admin,
} & RelayPaginationProp;

type State = {
  error: ?Error,
};

const PAGE_SIZE = 20;

class Students extends Component<Props, State> {
  state = {
    error: null,
  };

  handleEditStudent = (event, studentId) => {
    this.props.history.push(`/admin/student/${studentId}`);
    event.preventDefault();
  };

  handleLoadMore = () => {
    this.props.relay.loadMore(PAGE_SIZE, error => this.setState({ error }));
  };

  handleResetFilter = () => {
    this.props.actions.reset('studentsForm');
  };

  handleFilter = () => {
    const { hasStartedJobs, hasPendingJobRequests } = this.props.filterValues;

    this.props.relay.refetchConnection(
      PAGE_SIZE,
      error => {
        this.setState({ error });

        if (error) {
          // eslint-disable-next-line no-alert
          alert(error.message);
        }
      },
      {
        ...this.props.filterValues,
        hasStartedJobs: hasStartedJobs ? true : null,
        hasPendingJobRequests: hasPendingJobRequests ? true : null,
      }
    );

    this.props.actions.initialize('studentsForm', this.props.filterValues);
  };

  renderRow = ({ node }) => {
    const { email } = node.user;

    return (
      <TableRow key={node.id}>
        <TableRowColumn>
          <MailTo value={email} />
        </TableRowColumn>
        <TableRowColumn>
          <span>
            {node.firstname} {node.lastname}
            <IconButton href={`tel:${node.cellphoneNumber}`}>
              <PhoneIcon />
            </IconButton>
          </span>
        </TableRowColumn>

        <TableRowColumn>{node.pendingJobRequestsCount}</TableRowColumn>
        <TableRowColumn>{node.startedJobsCount}</TableRowColumn>

        <TableRowColumn>{node.user.status}</TableRowColumn>
        <TableRowColumn>
          <IconButton
            href={`/admin/student/${node.id}`}
            onClick={event => {
              this.handleEditStudent(event, node.id);
            }}
          >
            <CreateIcon />
          </IconButton>
        </TableRowColumn>
      </TableRow>
    );
  };

  render() {
    const { allStudents } = this.props.admin;
    const { error } = this.state;

    const { edges, pageInfo } = allStudents;

    const headers = [
      'Email',
      'Name',
      'Pending Requests',
      'Started Jobs',
      'Status',
      'Actions',
    ];

    return (
      <div>
        {error && error.message}
        <StudentsFilter
          handleFilter={this.handleFilter}
          handleResetFilter={this.handleResetFilter}
          filterValues={this.props.filterValues}
        />

        <Grid headers={headers} renderRow={this.renderRow} rows={edges} />

        <RaisedButton
          disabled={!pageInfo.hasNextPage}
          label="Load More"
          secondary
          onClick={this.handleLoadMore}
        />
      </div>
    );
  }
}

const selector = formValueSelector('studentsForm');

const query = graphql`
  query StudentsAdminQuery(
    $count: Int!
    $cursor: String
    $hasStartedJobs: Boolean
    $hasPendingJobRequests: Boolean
    $name: String
    $email: String
  ) {
    admin {
      ...Students_admin @arguments(count: $count, cursor: $cursor)
    }
  }
`;

export default enhancer(Students, {
  form: {
    form: 'studentsForm',
  },
  mapStateToProps: state => ({
    filterValues: selector(
      state,
      'name',
      'email',
      'status',
      'hasStartedJobs',
      'hasPendingJobRequests'
    ),
  }),
  mapDispatchToProps: dispatch => ({
    actions: bindActionCreators({ initialize, reset }, dispatch),
  }),
  relay: {
    variables: {
      count: PAGE_SIZE,
    },
    query,
    create: () =>
      createPaginationContainer(
        Students,
        {
          admin: graphql`
            fragment Students_admin on Admin
              @argumentDefinitions(
                count: { type: "Int" }
                cursor: { type: "String" }
              ) {
              allStudents(
                first: $count
                after: $cursor
                hasStartedJobs: $hasStartedJobs
                hasPendingJobRequests: $hasPendingJobRequests
                name: $name
                email: $email
              ) @connection(key: "Students_allStudents") {
                edges {
                  node {
                    id
                    firstname
                    lastname
                    pendingJobRequestsCount
                    startedJobsCount
                    cellphoneNumber
                    user {
                      email
                      active
                      status
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                }
              }
            }
          `,
        },
        {
          direction: 'forward',
          getConnectionFromProps(props) {
            return props.admin && props.admin.allStudents;
          },
          getFragmentVariables(prevVars, totalCount) {
            return {
              ...prevVars,
              count: totalCount,
            };
          },
          getVariables(props, { count, cursor } /* , fragmentVariables */) {
            return {
              count,
              cursor,
            };
          },
          query,
        }
      ),
  },
});



// WEBPACK FOOTER //
// ./src/modules/admin/student/Students.js